import React from 'react';
import { Box } from '@mui/material';
import { useOtpFlow } from '../hooks';
import DeliverySelector from './DeliverySelector';
import OtpCodeInput from './OtpCodeInput';
import OtpErrorDisplay from './OtpErrorDisplay';
import InvalidLinkNotice from './InvalidLinkNotice';
import { StaticHeader } from 'src/components/common/Header';

type OtpContentProps = ReturnType<typeof useOtpFlow>;

const OtpContent: React.FC<OtpContentProps> = ({
  attemptsRemaining,
  handleRetry,
  handleSendOtp,
  handleVerifyOtp,
  isLoading,
  otpError,
  otpErrorCode,
  otpStage,
  reportParam,
  reportPayload,
  selectedDelivery,
  setSelectedDelivery,
}) => {
  if (!reportPayload) {
    if (!reportParam) {
      return <InvalidLinkNotice />;
    }
    return null; // Still decoding
  }

  if (otpStage === 'delivery_select') {
    return (
      <DeliverySelector
        report={reportPayload}
        selected={selectedDelivery}
        onChange={setSelectedDelivery}
        onSend={handleSendOtp}
        isLoading={isLoading}
      />
    );
  }

  if (otpStage === 'code_entry' || otpStage === 'verifying') {
    return (
      <OtpCodeInput
        onVerify={handleVerifyOtp}
        onResend={handleSendOtp}
        isLoading={isLoading}
        attemptsRemaining={attemptsRemaining}
        error={otpError}
      />
    );
  }

  if (otpStage === 'error') {
    return (
      <OtpErrorDisplay
        error={otpError ?? 'An error occurred'}
        errorCode={otpErrorCode}
        onRetry={handleRetry}
      />
    );
  }

  return null;
};

const OtpFlow: React.FC = () => {
  const otpFlow = useOtpFlow();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <StaticHeader>
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            mb: 2,
            mt: 1,
            px: 4,
            py: 2,
            width: '100vw',
          }}
        >
          <img src="/bright/logo.svg" alt="Bright MLS" style={{ height: 40 }} />
          <Box
            sx={{
              backgroundColor: 'divider',
              flexGrow: 1,
              height: '1px',
              ml: 2,
            }}
          />
        </Box>
      </StaticHeader>
      <Box sx={{ p: 4, maxWidth: '38rem', ml: { xs: 0, md: 4 } }}>
        <OtpContent {...otpFlow} />
      </Box>
    </Box>
  );
};

export default OtpFlow;
